import { TFunction } from "i18next";
import defaultContent from "../../content/common";

/**
 * Checks if a name is valid (not empty, null, undefined, whitespace, or "--")
 */
const isValidName = (n: string | undefined | null): boolean => {
  return !!n && n.trim() !== "" && n !== "--";
};

/**
 * Checks if user's locale matches the ABO/applicant's language code
 */
const shouldUseLocalName = (locale: string, isoLanguageCode?: string | null): boolean => {
  const userLanguage = locale.split("-")[0].toLowerCase();
  const aboLanguage = isoLanguageCode?.toLowerCase() || "";
  return userLanguage === aboLanguage;
};

/**
 * Resolves ABO display name with locale selection and privacy flag handling.
 *
 * Business rules:
 * - Privacy flag returns confidential text
 * - Locale matches: localName → name → N/A
 * - Locale doesn't match: name → N/A (no fallback to localName)
 */
export const resolveDisplayName = (params: {
  name: string | undefined | null;
  localName: string | undefined | null;
  privacyFlag: boolean | undefined | null;
  languageCode: string | undefined | null;
  locale: string;
  confidentialText: string;
  notAvailableText: string;
}): string => {
  const { name, localName, privacyFlag, languageCode, locale, confidentialText, notAvailableText } = params;

  // Privacy flag check
  if (privacyFlag === true) {
    return confidentialText;
  }

  // Locale-based name selection with one-way fallback
  if (shouldUseLocalName(locale, languageCode)) {
    // Locale matches: localName → name → N/A
    if (isValidName(localName)) return localName!;
    if (isValidName(name)) return name!;
    return notAvailableText;
  }

  // Locale doesn't match: name → N/A (no fallback to localName)
  if (isValidName(name)) return name!;
  return notAvailableText;
};

/**
 * Resolves applicant display name with name format pattern support.
 * Handles format patterns like "Given Family" or "Family Given".
 */
export const resolveApplicantDisplayName = (
  displayName: string,
  localeDisplayName: string,
  localeName: { givenName: string; familyName: string },
  latinName: { givenName: string; familyName: string },
  nameFormat: string,
  privacyFlag: boolean,
  preferredLanguageCode: string,
  portalLocale: string,
  t: TFunction,
): string => {
  // Privacy flag check
  if (privacyFlag === true) {
    return t("common:confidentialUser", defaultContent["confidentialUser"]) as string;
  }

  // Determine which name to use based on locale
  const localeMatches = shouldUseLocalName(portalLocale, preferredLanguageCode);
  const selectedDisplayName = localeMatches ? localeDisplayName : displayName;
  if (isValidName(selectedDisplayName)) {
    return selectedDisplayName;
  }

  return t("common:notAvailable", defaultContent["notAvailable"]) as string;
};

/**
 * Translates backend-resolved display name.
 * Backend has already handled privacy and locale logic.
 * Frontend only translates special values: "Confidential User" and "N/A".
 */
export const getDisplayName = (displayName: string | undefined | null, t: TFunction): string => {
  if (displayName === "Confidential User") {
    return t("common:confidentialUser", defaultContent["confidentialUser"]) as string;
  }

  if (displayName === "N/A" || !displayName || displayName.trim() === "") {
    return t("common:notAvailable", defaultContent["notAvailable"]) as string;
  }

  return displayName;
};
